import {faBox} from "@fortawesome/pro-solid-svg-icons/faBox";
import {faEnvelope} from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import {faExpand} from "@fortawesome/pro-solid-svg-icons/faExpand";
import {faPaperPlane} from "@fortawesome/pro-solid-svg-icons/faPaperPlane";
import {faShare} from "@fortawesome/pro-solid-svg-icons/faShare";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useClientAccountOverview} from "@twigsby/client-data";
import {
    ColourSolid,
    ContentLoader,
    Pill,
    PillSize,
    Tile
} from "@twigsby/frontend-framework";
import React from "react";
import {useTranslation} from "@twigsby/frontend-framework";
import {useNavigate} from "react-router-dom";
import {EndpointError} from "../EndpointError";

export enum UserAccountStatsUnread {
    'Unread Mail' = 1,
    'Unread Mail Scans' = 2,
    'Unread Parcels' = 3,
    'Unread Parcel Scans' = 4,
}

const UserAccountStats = () => {
    const navigate = useNavigate();
    const { data, status, error } = useClientAccountOverview();
    const { t } = useTranslation();

    return (
        <>
            <h6 className={'sub-heading mb-xs'}>{t('accountStats.title')}</h6>
            {status === "error" && (
                <EndpointError {...error} />
            )}
            <div className={'user-account-stats'}>
                {status === "loading" && (
                    <>
                        <ContentLoader rounded={true} childCount={1} childHeight={'18.8em'} />
                        <ContentLoader rounded={true} childCount={1} childHeight={'18.8em'} />
                        <ContentLoader rounded={true} childCount={1} childHeight={'18.8em'} />
                        <ContentLoader rounded={true} childCount={1} childHeight={'18.8em'} />
                        <ContentLoader rounded={true} childCount={1} childHeight={'18.8em'} />
                        <ContentLoader rounded={true} childCount={1} childHeight={'18.8em'} />
                    </>
                )}
                {status !== "loading" && status !== "error" && (
                    <>
                        <AccountStatsEntry
                            label={t('accountStats.labels.mail')}
                            value={data?.counts?.unreadLetterEnvelopeScanCount}
                            badgeLabel={t('status.new')}
                            icon={<FontAwesomeIcon icon={faEnvelope} />}
                            callback={() => navigate('./mail/all', { state: { folderId: 8, includeFolderAssigned: true, unreadStatus: 1, mailType: 1 }})}
                        />
                        <AccountStatsEntry
                            label={t('accountStats.labels.mailScans')}
                            value={data?.counts?.unreadLetterContentScanCount}
                            badgeLabel={t('status.new')}
                            icon={<FontAwesomeIcon icon={faExpand} />}
                            callback={() => navigate('./mail/all', { state: { folderId: 8, includeFolderAssigned: true, unreadStatus: 2, mailType: 1 }})}
                        />
                        <AccountStatsEntry
                            label={t('accountStats.labels.parcels')}
                            value={data?.counts?.unreadParcelEnvelopeScanCount}
                            badgeLabel={t('status.new')}
                            icon={<FontAwesomeIcon icon={faBox} />}
                            callback={() => navigate('./mail/all', { state: { folderId: 8, includeFolderAssigned: true, unreadStatus: 1, mailType: 0 }})}
                        />
                        <AccountStatsEntry
                            label={t('accountStats.labels.parcelsScans')}
                            value={data?.counts?.unreadParcelContentScanCount}
                            badgeLabel={t('status.new')}
                            icon={
                                <div className={'icon-stack'}>
                                    <FontAwesomeIcon icon={faExpand} />
                                    <FontAwesomeIcon icon={faBox} />
                                </div>
                            }
                            callback={() => navigate('./mail/all', { state: { folderId: 8, includeFolderAssigned: true, unreadStatus: 2, mailType: 0 }})}
                        />
                        <AccountStatsEntry
                            label={t('accountStats.labels.sends')}
                            value={data?.counts?.pendingSendCount}
                            badgeLabel={t('status.processing')}
                            icon={<FontAwesomeIcon icon={faPaperPlane} />}
                            callback={() => navigate('./mail/sends')}
                        />
                        <AccountStatsEntry
                            label={t('accountStats.labels.forwarding')}
                            value={data?.counts?.pendingBatchCount}
                            badgeLabel={t('status.processing')}
                            icon={<FontAwesomeIcon icon={faShare} />}
                            callback={() => navigate('./mail/forwarding-batches', { state: { 'status': 2 }})}
                        />
                    </>
                )}
            </div>
        </>
    )
}

export { UserAccountStats }

type AccountStatsEntryType = {
    value: number;
    label: string;
    icon: any;
    badgeLabel: string;
    callback: () => void;
}

const AccountStatsEntry = (props: AccountStatsEntryType) => {
    const newValue = props.value || 0;

    return (
        <Tile
            title={props.label}
            description={newValue.toString()}
            media={props.icon}
            badge={newValue > 0 && <Pill content={props.badgeLabel} colour={ColourSolid.Warning} size={PillSize.Small} />}
            colour={newValue === 0 ? ColourSolid.Secondary : ColourSolid.Primary}
            onClick={() => props.callback()}
            disabled={newValue === 0}
        />
    )
}
